import { useState, useEffect, useRef, useCallback } from 'react';
import useScrollReveal from '../useScrollReveal';
import { sectionStyles, getAnimation } from '../utils';
import SectionDivider from './SectionDivider';

export default function AnimatedTextSection({ block = {}, primaryColor, secondaryColor, enableAnimations }) {
    const { config = {}, content = {} } = block;
    const variant = config.variant || 'centered';
    const s = sectionStyles(config, primaryColor, secondaryColor);
    const [ref, visible] = useScrollReveal(0.15, !enableAnimations);
    const anim = getAnimation(config, visible);
    const prefix = content.prefix || 'Somos expertos en';
    const words = (content.words || '').split('\n').filter(Boolean);
    const subtitle = content.subtitle || '';
    const speed = config.speed === 'fast' ? 2000 : config.speed === 'slow' ? 5000 : 3000;
    const [wordIndex, setWordIndex] = useState(0);
    const [charIndex, setCharIndex] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);

    /* ── Shared typewriter logic for 'centered' and 'typewriter' variants ── */
    useEffect(() => {
        if (words.length === 0 || !enableAnimations) return;
        if (variant !== 'centered' && variant !== 'typewriter') return;
        const word = words[wordIndex] || '';
        const typingSpeed = variant === 'typewriter' ? 60 : 100;
        const deletingSpeed = variant === 'typewriter' ? 40 : 50;
        const pauseTime = variant === 'typewriter' ? 1200 : 1500;
        const timeout = setTimeout(() => {
            if (!isDeleting) {
                if (charIndex < word.length) {
                    setCharIndex(c => c + 1);
                } else {
                    setTimeout(() => setIsDeleting(true), pauseTime);
                }
            } else {
                if (charIndex > 0) {
                    setCharIndex(c => c - 1);
                } else {
                    setIsDeleting(false);
                    setWordIndex(i => (i + 1) % words.length);
                }
            }
        }, isDeleting ? deletingSpeed : typingSpeed);
        return () => clearTimeout(timeout);
    }, [charIndex, isDeleting, wordIndex, words, enableAnimations, variant]);

    /* ── Split variant: word cycling with slide-up animation ── */
    const [splitWordIdx, setSplitWordIdx] = useState(0);
    const [splitAnim, setSplitAnim] = useState('enter');

    useEffect(() => {
        if (variant !== 'split' || words.length === 0 || !enableAnimations) return;
        const interval = setInterval(() => {
            setSplitAnim('exit');
            setTimeout(() => {
                setSplitWordIdx(i => (i + 1) % words.length);
                setSplitAnim('enter');
            }, 400);
        }, speed);
        return () => clearInterval(interval);
    }, [variant, words.length, speed, enableAnimations]);

    const currentWord = words[wordIndex] || '';
    const displayText = enableAnimations ? currentWord.slice(0, charIndex) : words[0] || '';

    const cls = `${s.padClass} relative ${s.radiusClass} ${s.shadowClass}`;
    const bgS = s.bgStyle.background ? s.bgStyle : { backgroundColor: s.bg || '#ffffff', ...s.bgStyle };

    /* ══════════════════ VARIANT: split ══════════════════ */
    if (variant === 'split') {
        const splitWord = words[splitWordIdx] || '';
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 py-16 ${anim}`}>
                    <div className="flex flex-col md:flex-row md:items-center md:gap-6 lg:gap-10">
                        {/* Left: prefix */}
                        <div className="md:w-1/2 text-center md:text-right mb-4 md:mb-0">
                            <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                                style={{ color: s.text }}>
                                {prefix}
                            </h2>
                        </div>
                        {/* Right: animated word */}
                        <div className="md:w-1/2 text-center md:text-left overflow-hidden" style={{ minHeight: '1.4em' }}>
                            <span
                                className={`inline-block text-3xl md:text-4xl lg:text-5xl font-bold leading-tight transition-all duration-400`}
                                style={{
                                    color: s.accent,
                                    transform: splitAnim === 'enter' ? 'translateY(0)' : 'translateY(100%)',
                                    opacity: splitAnim === 'enter' ? 1 : 0,
                                    transition: 'transform 0.4s ease, opacity 0.4s ease',
                                }}
                            >
                                {splitWord}
                            </span>
                        </div>
                    </div>
                    {subtitle && (
                        <p className="text-lg mt-6 max-w-3xl mx-auto text-center" style={{ color: s.textSub }}>{subtitle}</p>
                    )}
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ══════════════════ VARIANT: typewriter ══════════════════ */
    if (variant === 'typewriter') {
        return (
            <section className={cls} style={bgS}>
                <SectionDivider type={config.divider_top} color="transparent" position="top" config={config}/>
                <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 ${anim}`}>
                    <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                        style={{ color: s.text, fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace' }}>
                        {prefix}{' '}
                        <span style={{ color: s.accent, borderRight: '2px solid', borderColor: s.accent, paddingRight: '2px', animation: 'lmBlink 0.7s step-end infinite' }}>
                            {displayText}
                        </span>
                    </h2>
                    {subtitle && (
                        <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: s.textSub }}>{subtitle}</p>
                    )}
                    <style>{`@keyframes lmBlink{0%,100%{border-color:${s.accent}}50%{border-color:transparent}}`}</style>
                </div>
                <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom" config={config}/>
            </section>
        );
    }

    /* ══════════════════ VARIANT: centered (default) ══════════════════ */
    return (
        <section className={cls} style={bgS}>
            <SectionDivider type={config.divider_top} color="transparent" position="top"  config={config}/>
            <div ref={ref} className={`${s.maxWidth} mx-auto px-4 sm:px-6 lg:px-8 text-center py-16 ${anim}`}>
                <h2 className={`text-3xl md:text-4xl lg:text-5xl font-bold leading-tight transition-all duration-500 ${visible ? 'opacity-100' : 'opacity-0'}`}
                    style={{ color: s.text }}>
                    {prefix}{' '}
                    <span className="inline-block min-w-[1ch]" style={{ color: s.accent }}>
                        {displayText}
                        <span className="animate-pulse">|</span>
                    </span>
                </h2>
                {subtitle && (
                    <p className="text-lg mt-4 max-w-2xl mx-auto" style={{ color: s.textSub }}>{subtitle}</p>
                )}
            </div>
            <SectionDivider type={config.divider_bottom} color={config.divider_bottom_color || '#ffffff'} position="bottom"  config={config}/>
        </section>
    );
}
